export const CHAR_MAP = [
  ['/', 'SLASH'],
  ['_', 'UNDERSCORE'],
  ['-', 'DASH'],
  ['{', 'LBRACE'],
  ['}', 'RBRACE'],
] as const;

export const METHOD_SPLIT_POINT = 'METHOD';
const METHOD_SPLIT_POINT_CHAR = ':';

type CharMapType = typeof CHAR_MAP;

type ReplaceAll<
  S extends string,
  From extends string,
  To extends string,
> = S extends `${infer Left}${From}${infer Right}`
  ? `${Left}${To}${ReplaceAll<Right, From, To>}`
  : S;

type ApiMethodName<S extends string> = ReplaceAll<
  ReplaceAll<
    ReplaceAll<
      ReplaceAll<
        ReplaceAll<
          ReplaceAll<S, CharMapType[0][1], CharMapType[0][0]>,
          CharMapType[1][1],
          CharMapType[1][0]
        >,
        CharMapType[2][1],
        CharMapType[2][0]
      >,
      CharMapType[3][1],
      CharMapType[3][0]
    >,
    CharMapType[4][1],
    CharMapType[4][0]
  >,
  typeof METHOD_SPLIT_POINT,
  typeof METHOD_SPLIT_POINT_CHAR
>;

type IncludeOnly<T, S extends string> = {
  [K in keyof T as K extends string
    ? K extends `${string}${S}`
      ? K
      : never
    : never]: T[K];
};

type ExcludeOnly<T, S extends string> = {
  [K in keyof T as K extends `${string}${S}` ? never : K]: T[K];
};

type ApiMethodObject<T> = {
  [K in keyof T as ApiMethodName<K & string>]: T[K];
};

const restoreApiMethodName = (key: string): string => {
  let result = key;

  CHAR_MAP.forEach(([original, mapped]) => {
    result = result.replaceAll(mapped, original);
  });

  result = result.replace(METHOD_SPLIT_POINT, METHOD_SPLIT_POINT_CHAR);

  return result;
};

export const createApiMethodObject = <T extends Record<string, unknown>>(
  obj: T,
) => {
  const result: Record<string, unknown> = {};

  Object.entries(obj).forEach(([key, value]) => {
    const restoredKey = restoreApiMethodName(key);
    result[restoredKey] = value;
  });

  return result as ApiMethodObject<T>;
};

type OptionalParams<T> = T extends (params: infer A) => infer R
  ? (params?: A) => R
  : never;

export const createInfiniteQueryKeyObject = <T extends Record<string, unknown>>(
  obj: T,
) => {
  const result: Record<string, unknown> = {};
  const suffix = 'InfiniteQueryKey';
  type Suffix = typeof suffix;

  Object.entries(obj).forEach(([key, value]) => {
    if (key.endsWith(suffix)) {
      const restoredKey = restoreApiMethodName(key);

      const newName = restoredKey.slice(0, -suffix.length);

      result[newName] = value;
    }
  });

  type Name<S extends string> = S extends `${infer Rest}${Suffix}` ? Rest : S;

  return result as {
    [K in keyof IncludeOnly<T, Suffix> as Name<
      ApiMethodName<K & string>
    >]: OptionalParams<T[K]>;
  };
};

export const createInfiniteQueryOptionsObject = <
  T extends Record<string, unknown>,
>(
  obj: T,
) => {
  const result: Record<string, unknown> = {};
  const suffix = 'InfiniteOptions';
  type Suffix = typeof suffix;
  Object.entries(obj).forEach(([key, value]) => {
    if (key.endsWith(suffix)) {
      const restoredKey = restoreApiMethodName(key);

      const newName = restoredKey.slice(0, -suffix.length);

      result[newName] = value;
    }
  });

  type Name<S extends string> = S extends `${infer Rest}${typeof suffix}`
    ? Rest
    : S;

  return result as {
    [K in keyof IncludeOnly<T, Suffix> as Name<
      ApiMethodName<K & string>
    >]: T[K];
  };
};

export const createQueryOptionsObject = <T extends Record<string, unknown>>(
  obj: T,
) => {
  const result: Record<string, unknown> = {};
  const suffix = 'Options';
  type Suffix = typeof suffix;
  const excludeSuffix = 'Infinite';

  Object.entries(obj).forEach(([key, value]) => {
    if (key.endsWith(suffix)) {
      const restoredKey = restoreApiMethodName(key);

      const newName = restoredKey.slice(0, -suffix.length);

      if (newName.endsWith(excludeSuffix)) return;

      result[newName] = value;
    }
  });

  type Name<S extends string> = S extends `${infer Rest}${typeof suffix}`
    ? Rest
    : S;

  return result as ExcludeOnly<
    {
      [K in keyof IncludeOnly<T, Suffix> as Name<
        ApiMethodName<K & string>
      >]: T[K];
    },
    typeof excludeSuffix
  >;
};

export const createMutation = <T extends Record<string, unknown>>(obj: T) => {
  const result: Record<string, unknown> = {};
  const suffix = 'Mutation';
  type Suffix = typeof suffix;
  Object.entries(obj).forEach(([key, value]) => {
    if (key.endsWith(suffix)) {
      const restoredKey = restoreApiMethodName(key);

      const newName = restoredKey.slice(0, -suffix.length);

      result[newName] = value;
    }
  });

  type Name<S extends string> = S extends `${infer Rest}${typeof suffix}`
    ? Rest
    : S;

  return result as {
    [K in keyof IncludeOnly<T, Suffix> as Name<
      ApiMethodName<K & string>
    >]: T[K];
  };
};

export const createQueryKeyObject = <T extends Record<string, unknown>>(
  obj: T,
) => {
  const result: Record<string, unknown> = {};
  const suffix = 'QueryKey';
  type Suffix = typeof suffix;
  const excludeSuffix = 'Infinite';

  Object.entries(obj).forEach(([key, value]) => {
    if (key.endsWith(suffix)) {
      const restoredKey = restoreApiMethodName(key);

      const newName = restoredKey.slice(0, -suffix.length);

      if (newName.endsWith(excludeSuffix)) return;

      result[newName] = value;
    }
  });

  type Name<S extends string> = S extends `${infer Rest}${typeof suffix}`
    ? Rest
    : S;

  return result as ExcludeOnly<
    {
      [K in keyof IncludeOnly<T, Suffix> as Name<
        ApiMethodName<K & string>
      >]: OptionalParams<T[K]>;
    },
    typeof excludeSuffix
  >;
};
